import React, { useState, useEffect } from 'react';
import {
  Select,
  Stack,
  Button,
  Tooltip,
  Icon,
  TextStyle,
  ButtonGroup,
  Badge
} from '@shopify/polaris';
import { QuestionMarkMajor } from '@shopify/polaris-icons';
import { useTemplate } from '../../contexts/TemplateContext';

/**
 * A reusable component for selecting templates and workflows
 * 
 * @param {Object} props
 * @param {Array} props.initialSelectedTemplates - Initial array of selected template IDs
 * @param {Function} props.onTemplateSelect - Callback when templates are selected
 * @param {Function} props.onExecute - Callback when execute button is clicked
 * @param {boolean} props.disabled - Whether the component is disabled
 * @param {Array} props.selectedProducts - Array of selected products
 * @param {boolean} props.isExecuting - Whether template execution is in progress
 * @param {number} props.progress - Progress of template execution (0-100)
 * @param {Function} props.onOpenTemplateManager - Callback to open template manager
 * @param {Function} props.onOpenWorkflowManager - Callback to open workflow manager
 * @param {Function} props.onOpenHelp - Callback to open help modal
 */
export const TemplateSelector = ({
  initialSelectedTemplates = [],
  onTemplateSelect,
  onExecute,
  disabled = false,
  selectedProducts = [],
  isExecuting = false,
  progress = 0,
  onOpenTemplateManager,
  onOpenWorkflowManager,
  onOpenHelp
}) => {
  const { workflows, templates, aiModels } = useTemplate();
  const [selectedTemplates, setSelectedTemplates] = useState(initialSelectedTemplates);

  // Sync with parent component's selectedTemplates
  useEffect(() => {
    setSelectedTemplates(initialSelectedTemplates);
  }, [initialSelectedTemplates]);

  // Handle workflow selection
  const handleWorkflowChange = (value) => {
    if (!value) {
      setSelectedTemplates([]);
      onTemplateSelect([]);
      return;
    }
    
    const workflow = workflows?.find(w => w?.id === value);
    if (workflow?.templates) {
      setSelectedTemplates(workflow.templates);
      onTemplateSelect(workflow.templates);
    } else {
      setSelectedTemplates([]);
      onTemplateSelect([]);
    }
  };

  // Generate workflow options
  const getWorkflowOptions = () => {
    const options = [];
    
    // Add custom selection or empty state
    if (selectedTemplates.length > 0) {
      // Find if current selection matches any workflow
      const matchingWorkflow = workflows?.find(w => 
        w?.templates?.length === selectedTemplates.length && 
        w?.templates?.every((t, index) => t === selectedTemplates[index]));
      
      options.push({ 
        label: matchingWorkflow?.name || 'Custom Selection',
        value: matchingWorkflow?.id || 'custom'
      });
      
      // Add deselect option
      options.push({
        label: 'Deselect Workflow',
        value: ''
      });
    } else {
      options.push({
        label: 'Select a workflow',
        value: ''
      });
    }
    
    // Add all other workflows that don't match current selection
    if (workflows) {
      const otherWorkflows = workflows.filter(workflow => 
        !selectedTemplates.length || 
        !(workflow?.templates?.length === selectedTemplates.length && 
          workflow?.templates?.every((t, index) => t === selectedTemplates[index])));
          
      options.push(...otherWorkflows.map(workflow => ({
        label: workflow?.name || 'Unnamed Workflow',
        value: workflow?.id || ''
      })));
    }
    
    return options;
  };

  return (
    <Stack vertical spacing="loose">
      <Stack alignment="center" spacing="loose">
        <Stack.Item fill>
          <Select
            label="Select Workflow"
            helpText="Choose a workflow or create one in the Manage Workflows panel"
            options={getWorkflowOptions()}
            value={workflows?.find(w => 
              w?.templates?.length === selectedTemplates.length && 
              w?.templates?.every((t, index) => t === selectedTemplates[index]))?.id || 
              (selectedTemplates.length > 0 ? 'custom' : '')}
            onChange={handleWorkflowChange}
            disabled={disabled}
          />
        </Stack.Item>
        <Stack.Item>
          <ButtonGroup>
            <Button onClick={onOpenTemplateManager} disabled={disabled}>
              Manage Templates
            </Button>
            <Button onClick={onOpenWorkflowManager} disabled={disabled}>
              Manage Workflows
            </Button>
            <Tooltip content="Learn how to use the template workflow feature">
              <Button plain onClick={onOpenHelp}>
                <Icon source={QuestionMarkMajor} color="base" />
              </Button>
            </Tooltip>
            <Stack spacing="tight" alignment="center">
              {(() => {
                // Calculate total credit cost for all selected templates across all products
                const selectedProductCount = selectedProducts?.length || 0;
                
                if (selectedTemplates.length === 0 || selectedProductCount === 0) {
                  return null;
                }
                
                let totalCreditCost = 0;
                
                // For each template
                selectedTemplates.forEach(templateId => {
                  const template = templates?.find(t => t.id === templateId);
                  if (!template) return;
                  
                  // Tool templates cost 0 credits
                  if (template.type === 'tool') {
                    // Don't add any cost for tool templates
                    console.log('Skipping tool template in credit calculation:', template.name);
                  } else {
                    // Get output fields count
                    const outputFieldCount = (template.outputFields?.length || 0) + 
                                           (template.outputMetafields?.length || 0);
                    
                    // If there are no output fields or metafields, ensure at least 1 credit is charged
                    // This matches the logic in templateExecutionServiceV2.js
                    const effectiveOutputCount = Math.max(outputFieldCount, 1);
                    
                    // Get model cost per output
                    const model = template.modelId ? 
                                aiModels?.find(m => m.id === template.modelId) : 
                                aiModels?.find(m => m.isDefault);
                    
                    const costPerOutput = model?.creditCostPerOutput || 1;
                    
                    // Calculate cost for this template across all products
                    const templateTotalCost = effectiveOutputCount * costPerOutput * selectedProductCount;
                    totalCreditCost += templateTotalCost;
                  }
                });
                
                console.log('Total execution credit cost calculation:', {
                  selectedTemplates: selectedTemplates.length,
                  selectedProducts: selectedProductCount,
                  totalCreditCost
                });
                
                return (
                  <Tooltip content={`Total credit cost for executing ${selectedTemplates.length} template(s) across ${selectedProductCount} product(s)`}>
                    <Badge status="info">Total Credits: {totalCreditCost}</Badge>
                  </Tooltip>
                );
              })()}
              <Button
                primary
                onClick={onExecute}
                loading={isExecuting}
                disabled={selectedTemplates.length === 0 || isExecuting || disabled || !selectedProducts || selectedProducts.length === 0}
              >
                Execute
              </Button>
            </Stack>
          </ButtonGroup>
        </Stack.Item>
      </Stack>
      {isExecuting && (
        <TextStyle variation="subdued">
          Processing templates... {Math.round(progress * 100)}%
        </TextStyle>
      )}
    </Stack>
  );
};

export default TemplateSelector;
